import { FunctionComponent } from "react";
import { Link } from "react-router-dom";
import { styled, alpha } from "@mui/material/styles";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  Typography,
  Divider,
  SvgIcon,
} from "@mui/material";
import { MenuProps } from "@mui/material/Menu";
import { IProps } from "./IProps";
import { NestedMenuItem } from "mui-nested-menu";
import { Icon as MdiIcon } from "@mdi/react";

// -----------------------------

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
    transformOrigin={{ vertical: "top", horizontal: "right" }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    width: 260,
    color: "#00000099",
    fontWeight: 600,
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));

// -----------------------------

export const CustomMenu: FunctionComponent<IProps> = (props): JSX.Element => {
  return (
    <StyledMenu {...props}>
      {/* ROUTE LINKS */}
      {props.links?.map((link, i) => {
        const { Icon, label, href } = link;

        return (
          <Link
            key={i}
            to={href!}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <MenuItem sx={{ my: 1 }}>
              {Icon && (
                <ListItemIcon>
                  <MdiIcon path={Icon as string} size={0.8} />
                </ListItemIcon>
              )}
              <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
            </MenuItem>
          </Link>
        );
      })}

      {/* FIXED: no more rendering `0` */}
      {props.links && props.links.length > 0 && <Divider sx={{ my: 0.5 }} />}

      {/* FUNCTION LINKS */}
      {props.funcLinks?.map((link, i) => {
        const { Icon, label, onClick } = link;

        return (
          <MenuItem key={i} sx={{ my: 1 }} onClick={onClick}>
            {Icon && (
              <ListItemIcon>
                <SvgIcon>{Icon}</SvgIcon>
              </ListItemIcon>
            )}
            <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
          </MenuItem>
        );
      })}

      {/* NESTED LINKS */}
      {props.nestedLinks?.map((link, i) => {
        const { Icon, label, onClick } = link;

        return (
          <NestedMenuItem
            nonce
            key={i}
            parentMenuOpen
            label={label}
            leftIcon={Icon}
            sx={{ my: 1, ml: 1.7, fontWeight: 1000 }}
          >
            {link.sublinks.map((subLink, index) => (
              <MenuItem
                key={index}
                onClick={() => subLink.onClick?.(props.reportId)}
              >
                <ListItemIcon>
                  <SvgIcon>{subLink.Icon}</SvgIcon>
                </ListItemIcon>
                <Typography sx={{ fontWeight: 600 }}>
                  {subLink.label}
                </Typography>
              </MenuItem>
            ))}

            <Divider sx={{ my: 0.5 }} />

            {link.subFuncLinks.map((item, index) => (
              <MenuItem key={index} onClick={onClick}>
                <ListItemIcon>
                  <SvgIcon>{item.Icon}</SvgIcon>
                </ListItemIcon>
                <Typography sx={{ fontWeight: 600 }}>{item.label}</Typography>
              </MenuItem>
            ))}
          </NestedMenuItem>
        );
      })}
    </StyledMenu>
  );
};
